var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import classnames from 'classnames';
import { Option } from './Option';
import { Collapsible } from '../Collapsible';
import { Eyebrow } from '../Eyebrow';
import { Divider } from '../Divider';
import { Stack } from '../Stack';
import { Link } from '../Link';
export const OptionList = (_a) => {
    var { children, className, collapseValue, flatGroup, focusValue, groupClassName, indeterminateValue, multiple, onChange, onExpand, onGroupSelectAll, onGroupSelectNone, optionClassName, optionOnKeyDown, options, value, getOptionsData } = _a, props = __rest(_a, ["children", "className", "collapseValue", "flatGroup", "focusValue", "groupClassName", "indeterminateValue", "multiple", "onChange", "onExpand", "onGroupSelectAll", "onGroupSelectNone", "optionClassName", "optionOnKeyDown", "options", "value", "getOptionsData"]);
    const OptionsClasses = classnames('OptionList', className, {
        'OptionList--single-select': !multiple,
        'OptionList--multi-select': multiple,
    });
    const GroupClasses = classnames('OptionList__group', groupClassName);
    const ValuesFromArray = Array.isArray(value)
        ? value.map((v) => v.value !== undefined ? v.value : v)
        : null;
    const setDefaultOptionProperties = (option) => {
        let defaultSelected = false;
        if (value !== undefined &&
            option.value !== undefined &&
            ((Array.isArray(value) &&
                value.length > 0 &&
                ValuesFromArray.includes(option.value)) || // If value is an Array (multiple selections)
                (typeof value === 'object' && value.value === option.value)) // If value is an Object (single selection)
        ) {
            defaultSelected = true;
        }
        const defaults = {
            collapsed: option.collapsed ? option.collapsed : true,
            selected: defaultSelected,
            text: flatGroup && typeof option.content === 'string'
                ? option.content
                : option.text
                    ? option.text
                    : null,
            indeterminate: option.indeterminate || false,
        };
        return Object.assign(Object.assign({}, defaults), option);
    };
    const setInitialLevel = () => !flatGroup && (options === null || options === void 0 ? void 0 : options.some((option) => option.options)) ? 2 : 1;
    /* Loops through options object to display grouped and non-grouped options */
    const getOptions = (options, level = 2) => {
        return options === null || options === void 0 ? void 0 : options.map((optionProp, index) => {
            const localOption = setDefaultOptionProperties(optionProp);
            const checkForChildren = !!localOption.options;
            if (checkForChildren) {
                if (!flatGroup)
                    return getGroupedOptions(localOption, optionProp, localOption.options, index, level, optionProp.value, optionProp.collapsed);
                if (level === 1)
                    return getFlatGroupedOptions(localOption.options, localOption.text, level, index);
            }
            return getOption(localOption, optionProp, !flatGroup ? level : 1);
        });
    };
    /* Called when the options prop is detected in an individual option */
    const getGroupedOptions = (parentOption, originalParentOption, options, index, level, parentCollapseValue, collapsed) => {
        /* Variable determines if an option should be expanded to show children */
        const isOpen = collapsed
            ? !collapsed
            : collapseValue
                ? collapseValue.includes(parentCollapseValue)
                : true;
        return (React.createElement("div", { className: GroupClasses, role: "group", key: index },
            getOption(parentOption, originalParentOption, level, true, isOpen, options),
            React.createElement(Collapsible, { open: isOpen }, getOptions(options, level + 1))));
    };
    /* Pull flatGroup prop data for selection handling */
    const objectFlatGroup = typeof flatGroup === 'object' ? Object.assign({}, flatGroup) : undefined;
    const handleGroupSelectAll = (groupedOptions) => {
        if (objectFlatGroup.onGroupSelectAll === undefined)
            return;
        return objectFlatGroup.onGroupSelectAll(groupedOptions);
    };
    const handleGroupSelectNone = (groupedOptions) => {
        if (objectFlatGroup.onGroupSelectNone === undefined)
            return;
        return objectFlatGroup.onGroupSelectNone(groupedOptions);
    };
    const getFlatGroupedOptions = (options, title, level, index) => {
        const selectAll = () => handleGroupSelectAll(options);
        const selectNone = () => handleGroupSelectNone(options);
        return (React.createElement(React.Fragment, { key: `${index}${Object(options)}` },
            index !== 0 && React.createElement(Divider, { spacing: 1 }),
            title && (React.createElement(Stack, { alignItems: "center", className: "OptionList__header" },
                React.createElement(Eyebrow, null, title),
                multiple && title && typeof flatGroup !== 'boolean' && (React.createElement(React.Fragment, null,
                    React.createElement(Link, { primary: true, className: "m-l-1 fs-1", onClick: selectAll, "aria-label": "select all" }, "All"),
                    React.createElement(Link, { primary: true, className: "m-l-1 fs-1", onClick: selectNone, "aria-label": "select none" }, "None"))))),
            getOptions(options, level + 1)));
    };
    const handleOnChange = () => {
        if (onChange === undefined)
            return;
        return onChange;
    };
    const handleOnExpand = () => {
        if (onExpand === undefined)
            return;
        return onExpand;
    };
    /*
     ** NOTE: The following functions (setSelected, setFocus, registerOption, etc)
     ** are all plumbing to support the use case where <Option> components are
     ** rendered in user-land via function children.
     */
    const setSelectedFromOptionList = (optionValue) => {
        if (value == null || optionValue == null) {
            return false;
        }
        if (value.length) {
            return value.some((item) => {
                if (item === optionValue) {
                    return true;
                }
                if (item.value === optionValue) {
                    return true;
                }
                return false;
            });
        }
        if (value === optionValue) {
            return true;
        }
        if (value.value === optionValue) {
            return true;
        }
        return false;
    };
    const setFocusFromOptionList = (optionValue) => {
        return focusValue && focusValue.value === optionValue;
    };
    const setIndeterminateFromOptionList = (optionValue) => {
        return indeterminateValue === null || indeterminateValue === void 0 ? void 0 : indeterminateValue.includes(optionValue);
    };
    /*
     ** The <Option> components call back with registerOption once they've rendered
     */
    const optionsData = [];
    const registerOption = (optionData) => {
        optionsData.push(optionData);
    };
    React.useLayoutEffect(() => {
        optionsData.sort((a, b) => {
            if (a.element != null &&
                b.element != null &&
                a.element.compareDocumentPosition(b.element) &
                    Node.DOCUMENT_POSITION_PRECEDING) {
                return 1;
            }
            return -1;
        });
        getOptionsData === null || getOptionsData === void 0 ? void 0 : getOptionsData(optionsData);
    });
    /* Generates the individual option in the Option List */
    const getOption = (option, originalOption, level, collapseControl = false, collapseBoolean = false, localOptions = []) => {
        const _a = Object(option), { className } = _a, options = __rest(_a, ["className"]);
        const OptionClasses = classnames(className, optionClassName);
        return (React.createElement(Option, Object.assign({}, options, { className: OptionClasses, collapseControl: collapseControl, collapsed: collapseBoolean, focus: setFocusFromOptionList(Object(option).value), indeterminate: setIndeterminateFromOptionList(Object(option).value), key: `${Object(option).value}${Object(option).text}${level}`, level: level, onChange: handleOnChange(), onExpand: handleOnExpand(), onKeyDown: optionOnKeyDown, options: localOptions, selfObject: originalOption })));
    };
    const optionProps = {
        className: optionClassName,
        collapseControl: false,
        collapsed: false,
        getSelected: setSelectedFromOptionList,
        getFocus: setFocusFromOptionList,
        getIndeterminate: setIndeterminateFromOptionList,
        level: 1,
        onChange: handleOnChange(),
        onExpand: handleOnExpand(),
        onKeyDown: optionOnKeyDown,
        registerOption,
    };
    return (React.createElement("div", Object.assign({ className: OptionsClasses, "data-anvil-component": "OptionList", role: "tree" }, props), typeof children === 'function'
        ? children(optionProps)
        : getOptions(options, setInitialLevel())));
};
//# sourceMappingURL=OptionList.js.map